import React, { useEffect, useState } from "react";
import { cryptoPairLabel } from '../composables/fetchCrypto';
import { motion, AnimatePresence } from "framer-motion";
import { formatCryptoUsd } from "../utils/cryptoDecimal";

type Item = {
  id: string;
  symbol: string;
  price?: number;
  rank?: number; // เพิ่ม rank เพื่อใช้ในการเรียงลำดับ
  // PR #72 (Favorites Stock + Crypto): คริปโต symbol = X:BTCUSD, displaySymbol = BTC
  assetType?: 'STOCK' | 'CRYPTO';
  displaySymbol?: string;
  providerSymbol?: string;
};

const quoteSymbolOf = (item: Item) => item.providerSymbol || item.symbol;

type Props = {
  items: Item[];
  prices: { [symbol: string]: number };
  onRemove: (id: string) => void;
  onSelect: (symbol: string) => void;
  activeSymbol?: string | null; // ตัวที่ถูกเลือกจากภายนอก (ช่องค้นหา) ให้ขึ้นดาวเหมือนกดในลิสต์
};

export default function FavoriteSwipe({ items, prices, onRemove, onSelect, activeSymbol }: Props) {
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  useEffect(() => {
    if (activeSymbol) setSelectedSymbol(activeSymbol);
  }, [activeSymbol]);
const sortedItems = [...items].sort((a, b) => {
    const lastClickA = Number(localStorage.getItem(`last_click_${quoteSymbolOf(a)}`)) || 0;
    const lastClickB = Number(localStorage.getItem(`last_click_${quoteSymbolOf(b)}`)) || 0;

    // ถ้ามีการคลิก ให้เอาตัวที่คลิกล่าสุด (timestamp มากกว่า) ขึ้นก่อน
    if (lastClickA !== lastClickB) {
      return lastClickB - lastClickA;
    }
    // ถ้ายังไม่เคยคลิกเลย ให้เรียงตาม rank เดิมจาก API
    return (a.rank || 0) - (b.rank || 0);
  });
  const handleSelect = (symbol: string) => {
    setSelectedSymbol(symbol);
    localStorage.setItem('selectedSymbol', symbol);
    localStorage.setItem(`last_click_${symbol}`, Date.now().toString());
    onSelect(symbol);
  };

  return (
    <div className="mt-4 rounded-xl shadow-lg overflow-hidden" style={{ background: 'var(--component-bg-color)' }}>
      {/* --- 3. ครอบด้วย AnimatePresence เพื่อจัดการตอนเพิ่ม/ลบ items --- */}
      <AnimatePresence mode="popLayout">
        {sortedItems.map((item, idx) => (
          // --- 4. ครอบแต่ละแถวด้วย motion.div และใส่ prop `layout` ---
          <motion.div
            key={item.id} // สำคัญมาก! key ต้องห้ามซ้ำและคงที่
            layout // <--- พลังวิเศษอยู่ตรงนี้! มันจะคำนวณการเลื่อนให้เอง
            
            // แอนิเมชันตอนปรากฏ (Framer Motion จะจัดการให้ลื่นไหล)
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }} // แอนิเมชันตอนลบ
            
            // ปรับความลื่นไหลของแอนิเมชัน (Spring คือแบบเด้งๆ เหมือน iOS)
            transition={{
              type: "spring",
              stiffness: 350, // ความแข็งของสปริง (ยิ่งเยอะยิ่งเร็ว)
              damping: 30,     // ความหน่วง (ยิ่งเยอะยิ่งหยุดเร็ว ไม่เด้งนาน)
              opacity: { duration: 0.2 } // ปรับ opacity ให้เร็วหน่อย
            }}
            
            className="border-b last:border-none border-gray-700/50" // ย้าย border มาไว้ตรงนี้แทน StaticRow
          >
            <StaticRow
              id={item.id}
              symbol={quoteSymbolOf(item)}
              label={cryptoPairLabel(quoteSymbolOf(item)) || item.displaySymbol || item.symbol}
              isCrypto={item.assetType === 'CRYPTO' || /^X:/i.test(quoteSymbolOf(item))}
              price={prices[quoteSymbolOf(item)]}
              index={idx}
              onRemove={onRemove}
              onSelect={handleSelect} // ใช้ handleSelect ที่เก็บ timestamp
              isSelected={quoteSymbolOf(item) === selectedSymbol}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function StaticRow({
  id,
  symbol,
  label,
  isCrypto,
  price,
  index,
  onRemove,
  onSelect,
  isSelected,
}: {
  id: string;
  symbol: string;
  label: string;
  isCrypto?: boolean;
  price?: number;
  index: number;
  onRemove: (id: string) => void;
  onSelect: (symbol: string) => void;
  isSelected: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false); // State to track hover

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from propagating to parent div (onSelect)
    onRemove(id);
  };

  return (
    <div 
      className={`relative flex justify-between pl-2 pr-3 py-2 border-b last:border-none items-center h-[50px] cursor-pointer hover:opacity-80 transition-all ${isHovered ? 'bg-gray-100' : ''}`}
      style={{ 
        background: isHovered ? 'var(--hover-item-hover)' : 'var(--hover-item)', 
        color: 'var(--text-color)',
        boxShadow: 'var(--box-shadow)' 
      }}
      onClick={() => onSelect(symbol)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Index and Symbol */}
      <div className="flex items-center gap-1">
        <span className="w-5 text-xs font-medium opacity-40 text-center">
          {index + 1}
        </span>
        <div className="flex flex-col leading-tight">
          <div className="flex items-center gap-1">
            <span className="text-base font-bold tracking-wide">{label}</span>
            {isSelected && (
              <span style={{ color: 'var(--yellow-text-color)' }} className="text-sm animate-pulse">
                ★
              </span>
            )}
          </div>
          {/* ประเภทสินทรัพย์ใต้ชื่อ: Crypto (ส้ม) / Stock */}
          <span
            className="text-[9px] font-semibold"
            style={{ color:  'var(--text-color)', opacity: 0.55 }}
          >
            {isCrypto ? 'Crypto' : 'Stock'}
          </span>
        </div>
      </div>

      {/* Price */}
      <div className="text-right">
          {price !== undefined ? (
            <span className="font-mono font-semibold text-base"
            style={{color:"var(--yellow-text-color)"}}
            >
              ${isCrypto ? formatCryptoUsd(price) : price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </span>
          ) : (
            <div className="h-4 w-12 bg-gray-500/20 animate-pulse rounded"></div>
          )}
        </div>

      {/* Delete Button */}
      {isHovered && (
        <button
          onClick={handleDeleteClick}
          className="absolute left-0 top-0 translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
          aria-label="Remove"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24" 
            strokeWidth={2.5} 
            stroke="currentColor" 
            className="w-2 h-2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}